import React, { useState } from 'react';
import {
  Heart, Brain, Shield, Bone, Activity,
  TrendingUp, AlertTriangle,
  CheckCircle, Info, Home, User,
  BarChart3, Settings, Wind,
  ChevronRight, Filter, Zap,
  Droplets, Dumbbell
} from 'lucide-react';

// Định nghĩa màu sắc theo thiết kế Figma
const colors = {
  primary: '#28A049',     // Xanh lục chính
  blue: '#007BC2',        // Xanh dương chính
  orange: '#F37D42',      // Cam chính
  red: '#D20000',         // Đỏ báo động
  gray: '#6B7280',
  lightGray: '#F3F4F6',
  white: '#FFFFFF'
};

// Component biểu đồ tròn sức khỏe tổng thể
const HealthCircleChart = ({ percentage = 85, size = 120 }) => {
  const getHealthLevel = (percent) => {
    if (percent >= 85) return { color: colors.primary, label: 'Sức Khỏe Tốt' };
    if (percent >= 80) return { color: colors.blue, label: 'Sức Khỏe Khá' };
    if (percent >= 70) return { color: colors.orange, label: 'Cần Lưu Ý' };
    return { color: colors.red, label: 'Theo Dõi Sát' };
  };

  const health = getHealthLevel(percentage);
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex items-center justify-center">
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={colors.lightGray}
            strokeWidth="16"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={health.color}
            strokeWidth="16"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-4xl font-bold text-gray-800">{percentage}%</span>
        </div>
      </div>
    </div>
  );
};

// Component Circular Progress cho từng phân hệ
const SystemCircularProgress = ({ percentage = 85, size = 100, title }) => {
  const getColor = (percent) => {
    if (percent >= 85) return colors.primary;
    if (percent >= 80) return colors.blue;
    if (percent >= 70) return colors.orange;
    return colors.red;
  };

  const color = getColor(percentage);
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(40, 160, 73, 0.2)"
            strokeWidth="8"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold text-gray-800">{percentage}%</span>
        </div>
      </div>
      {title && <div className="mt-2 text-xs font-medium text-gray-600">{title}</div>}
    </div>
  );
};

// Component chi tiết xét nghiệm với progress bar
const TestDetailRow = ({ name, category, value, level, barPosition = 'center' }) => {
  const getLevelConfig = (level) => {
    switch(level) {
      case 'High': return { color: colors.red, text: 'High' };
      case 'Moderate': return { color: colors.orange, text: 'Moderate' };
      case 'Low': return { color: colors.blue, text: 'Low' };
      case 'Normal': return { color: colors.primary, text: 'Normal' };
      default: return { color: colors.primary, text: 'Normal' };
    }
  };

  const getBarPosition = (position, value) => {
    const normalizedValue = ((parseFloat(value) || 0) + 15) / 30 * 100;

    switch(position) {
      case 'left-wide':
        return { left: '15%', width: '35%' };
      case 'left':
        return { left: '35%', width: '15%' };
      case 'center':
        return { left: '47.5%', width: '5%' };
      case 'right-small':
        return { left: '55%', width: '10%' };
      case 'right':
        return { left: '25%', width: '25%' };
      default:
        return { left: `${Math.min(Math.max(normalizedValue - 2.5, 0), 95)}%`, width: '5%' };
    }
  };

  const config = getLevelConfig(level);
  const barStyle = getBarPosition(barPosition, value);

  return (
    <div className="flex items-center py-3">
      {/* Label */}
      <div className="w-48">
        <div className="font-semibold text-gray-800 text-sm">{name}</div>
        <div className="text-xs text-gray-500">{category}</div>
      </div>

      {/* Progress bar */}
      <div className="flex-1 mx-8">
        <div className="relative h-4">
          {/* Background */}
          <div className="w-full h-4 bg-blue-100 rounded-sm relative">
            {/* Scale markers */}
            <div className="absolute -bottom-5 left-0 text-xs text-gray-400">-15</div>
            <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 text-xs text-gray-400">0</div>
            <div className="absolute -bottom-5 right-0 text-xs text-gray-400">15</div>

            {/* Progress bar */}
            <div
              className="absolute top-0 h-4 rounded-sm"
              style={{
                backgroundColor: config.color,
                ...barStyle
              }}
            />

            {/* Red indicator line at center */}
            <div className="absolute w-0.5 h-6 bg-red-600 left-1/2 transform -translate-x-1/2 -top-1" />
          </div>
        </div>
      </div>

      {/* Value and Badge */}
      <div className="flex items-center w-32">
        <span
          className="font-bold text-lg text-right flex-1"
          style={{ color: config.color }}
        >
          {value}
        </span>
        <div
          className="px-3 py-1 rounded text-xs font-bold text-white text-center w-20 ml-3"
          style={{ backgroundColor: config.color }}
        >
          {config.text}
        </div>
      </div>
    </div>
  );
};

// Component biểu đồ radar
const RadarChart = ({ data, title, size = 200 }) => {
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.4;

  // Tạo các điểm cho đường viền
  const createRadarPoints = (values) => {
    return values.map((value, index) => {
      const angle = (index * 2 * Math.PI) / values.length - Math.PI / 2;
      const r = (value / 100) * radius;
      const x = centerX + r * Math.cos(angle);
      const y = centerY + r * Math.sin(angle);
      return `${x},${y}`;
    }).join(' ');
  };

  // Tạo các điểm cho labels
  const createLabelPoints = (labels) => {
    return labels.map((label, index) => {
      const angle = (index * 2 * Math.PI) / labels.length - Math.PI / 2;
      const x = centerX + (radius + 25) * Math.cos(angle);
      const y = centerY + (radius + 25) * Math.sin(angle);
      return { x, y, label: label.name, value: label.value, color: label.color };
    });
  };

  const radarPoints = createRadarPoints(data.map(d => d.value));
  const labelPoints = createLabelPoints(data);

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h3 className="text-lg font-semibold text-blue-600 mb-4 text-center">{title}</h3>
      <div className="flex items-center justify-center">
        <svg width={size + 60} height={size + 60} className="overflow-visible">
          {/* Grid circles */}
          {[1, 2, 3, 4, 5].map(level => (
            <circle
              key={level}
              cx={centerX}
              cy={centerY}
              r={(radius * level) / 5}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="1"
            />
          ))}

          {/* Grid lines */}
          {data.map((_, index) => {
            const angle = (index * 2 * Math.PI) / data.length - Math.PI / 2;
            const x2 = centerX + radius * Math.cos(angle);
            const y2 = centerY + radius * Math.sin(angle);
            return (
              <line
                key={index}
                x1={centerX}
                y1={centerY}
                x2={x2}
                y2={y2}
                stroke="#e2e8f0"
                strokeWidth="1"
              />
            );
          })}

          {/* Data area */}
          <polygon
            points={radarPoints}
            fill="rgba(59, 130, 246, 0.1)"
            stroke="#3b82f6"
            strokeWidth="2"
          />

          {/* Data points */}
          {labelPoints.map((point, index) => (
            <circle
              key={index}
              cx={centerX + ((data[index].value / 100) * radius) * Math.cos((index * 2 * Math.PI) / data.length - Math.PI / 2)}
              cy={centerY + ((data[index].value / 100) * radius) * Math.sin((index * 2 * Math.PI) / data.length - Math.PI / 2)}
              r="4"
              fill={point.color}
            />
          ))}

          {/* Labels */}
          {labelPoints.map((point, index) => (
            <g key={index}>
              <text
                x={point.x}
                y={point.y - 10}
                textAnchor="middle"
                className="text-xs font-medium fill-gray-700"
              >
                {point.label}
              </text>
              <text
                x={point.x}
                y={point.y + 5}
                textAnchor="middle"
                className="text-xs font-bold"
                fill={point.color}
              >
                {point.value}%
              </text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
};

// Component legend cho biểu đồ radar
const RadarLegend = ({ data, onSystemClick }) => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <h4 className="font-semibold mb-3">Phân hệ</h4>
      <div className="space-y-2">
        {data.map((item, index) => (
          <div
            key={index}
            className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
            onClick={() => onSystemClick && onSystemClick(item.name)}
          >
            <div className="flex items-center space-x-3">
              <item.icon className="h-4 w-4 text-gray-600" />
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-sm font-medium text-gray-700">{item.name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span
                className="text-sm font-bold"
                style={{ color: item.color }}
              >
                {item.value}%
              </span>
              {onSystemClick && <ChevronRight className="h-4 w-4 text-gray-400" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Component thanh trạng thái sức khỏe
const HealthStatusBar = () => {
  const healthLevels = [
    { label: 'Sức Khỏe Tốt', color: colors.primary },
    { label: 'Sức Khỏe Khá', color: colors.blue },
    { label: 'Cần Lưu Ý', color: colors.orange },
    { label: 'Theo Dõi Sát', color: colors.red }
  ];

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <h3 className="font-semibold mb-4 text-gray-800 text-center">Thang đánh giá tình trạng sức khỏe</h3>

      {/* 4 thanh ngang riêng biệt */}
      <div className="flex items-center justify-center space-x-1.5">
        {healthLevels.map((level, index) => (
          <div key={index} className="flex flex-col items-center">
            <div
              className="w-32 h-6 rounded-full"
              style={{ backgroundColor: level.color }}
            />
            <span
              className="text-xs font-medium mt-2 text-center"
              style={{ color: level.color }}
            >
              {level.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Component thống kê tổng hợp
const HealthSummaryStats = () => {
  const stats = [
    {
      icon: BarChart3,
      label: 'Tổng đánh giá',
      value: '254',
      color: colors.gray,
      bgColor: 'bg-gray-50'
    },
    {
      icon: CheckCircle,
      label: 'Tình Trạng Tốt',
      value: '174',
      color: colors.primary,
      bgColor: 'bg-green-50'
    },
    {
      icon: Info,
      label: 'Tình Trạng Khá',
      value: '22',
      color: colors.blue,
      bgColor: 'bg-blue-50'
    },
    {
      icon: AlertTriangle,
      label: 'Cần Lưu Ý',
      value: '34',
      color: colors.orange,
      bgColor: 'bg-orange-50'
    },
    {
      icon: AlertTriangle,
      label: 'Theo Dõi Sát',
      value: '24',
      color: colors.red,
      bgColor: 'bg-red-50'
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      {stats.map((stat, index) => (
        <div key={index} className={`${stat.bgColor} p-4 rounded-lg`}>
          <div className="flex items-center space-x-3">
            <stat.icon className="h-6 w-6" style={{ color: stat.color }} />
            <div>
              <div className="text-2xl font-bold" style={{ color: stat.color }}>
                {stat.value}
              </div>
              <div className="text-xs font-medium" style={{ color: stat.color }}>
                {stat.label}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Component nguy cơ tim mạch
const CardiovascularRisk = () => {
  const risks = [
    { label: '10-year CVD Risk', value: '0.8%', description: 'Nguy Cơ Thấp', type: 'CVD' },
    { label: 'ASCVD', value: '0.76%', description: 'Nguy Cơ Thấp', type: 'ASCVD' },
    { label: 'CVA', value: '0.45%', description: 'Nguy Cơ Thấp', type: 'CVA' },
    { label: 'CFH', value: '0.62%', description: 'Nguy Cơ Thấp', type: 'CFH' }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {risks.map((risk, index) => (
        <div key={index} className="bg-white p-4 rounded-lg border border-green-200 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-lg font-bold text-green-600">{risk.value}</span>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-green-600">{risk.label}</div>
            <div className="text-xs font-medium text-green-600">{risk.description}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Component navigation
const Navigation = ({ activeTab, setActiveTab }) => {
  const navItems = [
    { id: 'overview', label: 'Tổng Quan', icon: Home },
    { id: 'systems', label: 'Phân Hệ', icon: Zap },
    { id: 'trends', label: 'Kế hoạch', icon: TrendingUp }
  ];

  return (
    <nav className="bg-white shadow-sm mb-6">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex space-x-8">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex items-center space-x-2 py-4 border-b-2 transition-colors ${
                activeTab === item.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <item.icon className="h-5 w-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
};

// Component System Detail cho chi tiết phân hệ (dùng cho icon gan)
const LiverIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-8 w-8">
    <path d="M12 2C10.9 2 10 2.9 10 4C10 5.28 10.65 6.38 11.58 7C10.65 7.62 10 8.71 10 10C10 11.1 10.9 12 12 12C13.1 12 14 11.1 14 10C14 8.71 13.35 7.62 12.42 7C13.35 6.38 14 5.28 14 4C14 2.9 13.1 2 12 2M9 12C7.9 12 7 12.9 7 14C7 15.1 7.9 16 9 16C9.72 16 10.34 15.64 10.71 15.09L12 17L13.29 15.09C13.66 15.64 14.28 16 15 16C16.1 16 17 15.1 17 14C17 12.9 16.1 12 15 12C14.28 12 13.66 12.36 13.29 12.91L12 11L10.71 12.91C10.34 12.36 9.72 12 9 12M6 16C4.9 16 4 16.9 4 18C4 19.1 4.9 20 6 20C6.72 20 7.34 19.64 7.71 19.09L9 21L10.29 19.09C10.66 19.64 11.28 20 12 20C12.72 20 13.34 19.64 13.71 19.09L15 21L16.29 19.09C16.66 19.64 17.28 20 18 20C19.1 20 20 19.1 20 18C20 16.9 19.1 16 18 16C17.28 16 16.66 16.36 16.29 16.91L15 15L13.71 16.91C13.34 16.36 12.72 16 12 16C11.28 16 10.66 16.36 10.29 16.91L9 15L7.71 16.91C7.34 16.36 6.72 16 6 16Z"/>
  </svg>
);

// Component chính - Dashboard
const OriginalMedicalDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSystem, setSelectedSystem] = useState(null);

  // Thông tin bệnh nhân
  const patientInfo = {
    name: 'Nguyễn Phi Lê',
    healthScore: 85,
    doctorAssessment: 'Sức Khỏe Tốt',
    biologicalAge: 34.49,
    actualAge: 28.66
  };

  // Dữ liệu cho biểu đồ 7 hệ cơ quan với icons phù hợp hơn
  const organSystemsData = [
    { name: 'Thần Kinh', value: 78, color: colors.red, icon: Brain },
    { name: 'Tuần Hoàn', value: 81, color: colors.orange, icon: Heart },
    { name: 'Hô Hấp', value: 73, color: colors.red, icon: Wind },
    { name: 'Chuyển Hóa', value: 81, color: colors.orange, icon: LiverIcon },
    { name: 'Thận Niệu', value: 78, color: colors.red, icon: Droplets },
    { name: 'Cơ Xương Khớp', value: 77, color: colors.red, icon: Dumbbell },
    { name: 'Miễn Dịch', value: 85, color: colors.primary, icon: Shield }
  ];

  // Dữ liệu cho biểu đồ lối sống
  const lifestyleData = [
    { name: 'Dinh Dưỡng', value: 67, color: colors.red, icon: Activity },
    { name: 'Hoạt Động', value: 85, color: colors.primary, icon: Activity },
    { name: 'Phục Hồi', value: 80, color: colors.orange, icon: Wind },
    { name: 'Cảm Xúc', value: 100, color: colors.primary, icon: Heart },
    { name: 'Môi Trường', value: 85, color: colors.primary, icon: Home }
  ];

  // Handler cho click vào hệ cơ quan
  const handleSystemClick = (systemName) => {
    setSelectedSystem(systemName);
    setActiveTab('systems');
  };

  // Handler cho quay lại từ chi tiết
  const handleBackFromDetail = () => {
    setSelectedSystem(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Activity className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-blue-600">
                HỒ SƠ Y KHOA KHÁCH HÀNG
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-gray-500">Khách hàng:</div>
                <div className="font-semibold text-green-600">{patientInfo.name}</div>
              </div>
              <User className="h-8 w-8 text-gray-400" />
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <Navigation activeTab={activeTab} setActiveTab={setActiveTab} />

      <div className="max-w-7xl mx-auto px-4 pb-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Tổng quan sức khỏe */}
            <section>
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h2 className="text-lg font-semibold">TỔNG QUAN SỨC KHỎE</h2>
              </div>
              <div className="bg-white p-6 rounded-b-lg shadow-sm">
                <div className="flex items-start gap-12">
                  {/* Biểu đồ tròn bên trái - wrapped in card */}
                  <div className="flex-shrink-0">
                    <div className="bg-white p-8 shadow-lg rounded-2xl border border-gray-100 w-80 h-80 flex items-center justify-center">
                      <HealthCircleChart percentage={patientInfo.healthScore} size={200} />
                    </div>
                  </div>

                  {/* Thông tin chi tiết bên phải */}
                  <div className="flex-1 h-80 flex flex-col">
                    {/* 4 Cards thông tin với khoảng cách đều nhau */}
                    <div className="space-y-3">
                      {/* Card Khách hàng */}
                      <div className="bg-white p-4 shadow-lg rounded-2xl border border-gray-100 h-12 flex items-center">
                        <div className="flex items-baseline space-x-2">
                          <span className="text-gray-600 text-sm">Khách hàng:</span>
                          <span className="font-bold text-green-600 text-xl">{patientInfo.name}</span>
                        </div>
                      </div>

                      {/* Card Tình trạng sức khỏe */}
                      <div className="bg-white p-4 shadow-lg rounded-2xl border border-gray-100 h-12 flex items-center">
                        <div className="flex items-baseline space-x-2">
                          <span className="text-gray-600 text-sm">Tình trạng sức khỏe:</span>
                          <span className="font-bold text-green-600 text-xl">{patientInfo.healthScore}%</span>
                        </div>
                      </div>

                      {/* Card Đánh giá của bác sĩ */}
                      <div className="bg-white p-4 shadow-lg rounded-2xl border border-gray-100 h-12 flex items-center">
                        <div className="flex items-baseline space-x-2">
                          <span className="text-gray-600 text-sm">Đánh giá của Bác sĩ:</span>
                          <span className="font-bold text-green-600 text-xl">{patientInfo.doctorAssessment}</span>
                        </div>
                      </div>

                      {/* Thang đánh giá tình trạng sức khỏe */}
                      <div className="bg-white p-4 shadow-lg rounded-2xl border border-gray-100 flex flex-col justify-center" style={{ height: '140px' }}>
                        <div className="space-y-3">
                          <h3 className="text-gray-800 font-medium text-sm">Thang đánh giá tình trạng sức khỏe</h3>

                          {/* Labels và dots */}
                          <div className="grid grid-cols-4 gap-3">
                            <div className="flex items-center space-x-1">
                              <div className="w-2 h-2 rounded-full bg-green-600"></div>
                              <span className="text-green-600 font-medium text-xs">Sức Khỏe Tốt</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                              <span className="text-blue-600 font-medium text-xs">Sức Khỏe Khá</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                              <span className="text-orange-500 font-medium text-xs">Cần Lưu Ý</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-2 h-2 rounded-full bg-red-600"></div>
                              <span className="text-red-600 font-medium text-xs">Theo Dõi Sát</span>
                            </div>
                          </div>

                          {/* 4 thanh màu */}
                          <div className="grid grid-cols-4 gap-3">
                            <div className="h-3 bg-green-600 rounded-full"></div>
                            <div className="h-3 bg-blue-600 rounded-full"></div>
                            <div className="h-3 bg-orange-500 rounded-full"></div>
                            <div className="h-3 bg-red-600 rounded-full"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Thống kê tổng hợp */}
            <section>
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h2 className="text-lg font-semibold">TỔNG HỢP TÌNH TRẠNG SỨC KHỎE</h2>
              </div>
              <div className="bg-white p-6 rounded-b-lg shadow-sm">
                <HealthSummaryStats />
              </div>
            </section>

            {/* Nguy cơ tim mạch */}
            <section>
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h2 className="text-lg font-semibold">NGUY CƠ TIM MẠCH TRONG 10 NĂM</h2>
              </div>
              <div className="bg-white p-6 rounded-b-lg shadow-sm">
                <CardiovascularRisk />
              </div>
            </section>

            {/* Chỉ số sức khỏe cần cải thiện ngay */}
            <section>
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h2 className="text-lg font-semibold">CHỈ SỐ SỨC KHỎE CẦN CẢI THIỆN NGAY</h2>
              </div>
              <div className="bg-white p-6 rounded-b-lg shadow-sm">
                <div className="space-y-4">
                  {[
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '-5', level: 'High', barPosition: 'left' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '-1.0', level: 'Moderate', barPosition: 'left' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '-10', level: 'High', barPosition: 'left-wide' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '1.0', level: 'Low', barPosition: 'right-small' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '-0.5', level: 'Normal', barPosition: 'center' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '-0.5', level: 'Normal', barPosition: 'center' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' }
                  ].map((test, index) => (
                    <TestDetailRow key={index} {...test} />
                  ))}
                </div>
              </div>
            </section>

            {/* Tuổi sinh học */}
            <section>
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h2 className="text-lg font-semibold">TUỔI SINH HỌC</h2>
              </div>
              <div className="bg-white p-6 rounded-b-lg shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-500">34.49</div>
                      <div className="text-sm text-gray-600">So với 28.66 (Tuổi thật)</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {[
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' }
                  ].map((test, index) => (
                    <TestDetailRow key={index} {...test} />
                  ))}
                </div>
              </div>
            </section>

            {/* Biểu đồ Radar - 7 hệ cơ quan và Lối sống */}
            <section>
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h2 className="text-lg font-semibold">BIỂU ĐỒ TỔNG QUAN</h2>
              </div>
              <div className="bg-gray-50 p-6 rounded-b-lg">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {/* Biểu đồ 7 hệ cơ quan */}
                  <div className="flex gap-6">
                    <div className="flex-shrink-0">
                      <RadarChart
                        data={organSystemsData}
                        title="BIỂU ĐỒ TỔNG QUAN 7 HỆ CƠ QUAN"
                        size={280}
                      />
                    </div>
                    <div className="flex-1">
                      <RadarLegend data={organSystemsData} onSystemClick={handleSystemClick} />
                    </div>
                  </div>

                  {/* Biểu đồ lối sống */}
                  <div className="flex gap-6">
                    <div className="flex-shrink-0">
                      <RadarChart
                        data={lifestyleData}
                        title="BIỂU ĐỒ TỔNG QUAN LỐI SỐNG"
                        size={280}
                      />
                    </div>
                    <div className="flex-1">
                      <RadarLegend data={lifestyleData} />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'systems' && (
          <div className="space-y-8">
            {/* Chi tiết từng phân hệ */}
            <section>
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h2 className="text-lg font-semibold">CHI TIẾT ĐẦU MỤC 7 PHÂN HỆ CẦN LƯU Ý</h2>
              </div>
              <div className="bg-white p-6 rounded-b-lg shadow-sm space-y-8">
                {organSystemsData.map((system) => {
                  const Icon = system.icon;
                  const systemTests = {
                    'Chuyển Hóa': [
                      { name: 'Glucose Fasting', category: 'Metabolism', value: '1', level: 'Low', barPosition: 'right-small' },
                      { name: 'HbA1c', category: 'Metabolism', value: '0.5', level: 'Normal', barPosition: 'center' },
                      { name: 'Insulin Sensitivity', category: 'Metabolism', value: '-1', level: 'Moderate', barPosition: 'left' },
                      { name: 'Metabolic Rate', category: 'Metabolism', value: '0', level: 'Normal', barPosition: 'center' },
                      { name: 'Liver Function', category: 'Metabolism', value: '0.8', level: 'Low', barPosition: 'right-small' },
                      { name: 'Thyroid Function', category: 'Metabolism', value: '-0.5', level: 'Normal', barPosition: 'center' },
                      { name: 'Lipid Profile', category: 'Metabolism', value: '2', level: 'Moderate', barPosition: 'right' }
                    ]
                  };

                  const defaultTests = [
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' },
                    { name: 'Peak Expiratory Flow Rate', category: 'Pulmonary', value: '5', level: 'High', barPosition: 'right' }
                  ];

                  const tests = systemTests[system.name] || defaultTests;

                  return (
                    <div key={system.name} className="flex gap-6 p-4 border rounded-lg">
                      {/* Left side - System info */}
                      <div className="flex flex-col items-center" style={{ minWidth: '200px' }}>
                        {system.name === 'Chuyển Hóa' ? (
                          <LiverIcon />
                        ) : (
                          <system.icon className="h-8 w-8 mb-2" style={{ color: system.color }} />
                        )}
                        <div className="text-sm font-semibold mb-3">{system.name}</div>

                        {/* Circular chart */}
                        <SystemCircularProgress
                          percentage={system.value}
                          size={120}
                        />

                        {/* Details */}
                        <div className="mt-4 space-y-2 text-xs w-full">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Cấu Trúc:</span>
                            <span className="font-semibold" style={{ color: colors.primary }}>86,2%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Chức Năng:</span>
                            <span className="font-semibold" style={{ color: colors.primary }}>86,2%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Điểm Rủi Ro:</span>
                            <span className="font-semibold" style={{ color: colors.primary }}>9,2%</span>
                          </div>
                        </div>
                      </div>

                      {/* Right side - Test results */}
                      <div className="flex-1">
                        <div className="space-y-4">
                          {tests.map((test, index) => (
                            <TestDetailRow key={index} {...test} />
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'details' && (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center">
            <BarChart3 className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Chi Tiết Xét Nghiệm
            </h3>
            <p className="text-gray-500">
              Tính năng chi tiết xét nghiệm đang được phát triển
            </p>
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="space-y-6">
            {/* Header thông tin bệnh nhân */}
            <section>
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h2 className="text-lg font-semibold">KẾ HOẠCH CHĂM SÓC SỨC KHỎE 12 THÁNG (8/2025 - 8/2026)</h2>
              </div>
              <div className="bg-white p-6 rounded-b-lg shadow-sm">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded border shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">Khách hàng:</div>
                    <div className="font-bold text-green-600">Nguyễn Phi Lê</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded border shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">Bác sĩ phụ trách:</div>
                    <div className="font-bold text-green-600">Nguyễn Quốc Taylor</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded border shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">Năm sinh:</div>
                    <div className="font-bold text-green-600">1997 (28 tuổi)</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded border shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">Ngày lập kế hoạch:</div>
                    <div className="font-bold text-green-600">29/08/2025</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Bảng phân cấp mức độ chăm sóc */}
            <section>
              <div className="bg-gradient-to-r from-transparent via-blue-600 to-transparent text-white px-4 py-2 text-center">
                <h3 className="text-lg font-semibold">Bảng phân cấp mức độ chăm sóc</h3>
              </div>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Header */}
                <div className="grid grid-cols-4 bg-blue-600 text-white">
                  <div className="p-3 text-center font-semibold text-sm">Stt</div>
                  <div className="p-3 text-center font-semibold text-sm">Mức Độ</div>
                  <div className="p-3 text-center font-semibold text-sm">Định Nghĩa</div>
                  <div className="p-3 text-center font-semibold text-sm">Mô Tả</div>
                </div>

                {/* Rows */}
                <div className="grid grid-cols-4 bg-blue-50">
                  <div className="p-3 text-center font-semibold">1</div>
                  <div className="p-3 text-center font-bold text-green-600">Mức 1 - Phòng Ngừa</div>
                  <div className="p-3 text-center">Khỏe mạnh, nguy cơ thấp</div>
                  <div className="p-3 text-xs">Tập trung xây dựng thói quen tốt (dinh dưỡng, vận động, giấc ngủ) để phòng tránh bệnh</div>
                </div>

                <div className="grid grid-cols-4 bg-blue-50 border-t">
                  <div className="p-3 text-center font-semibold">2</div>
                  <div className="p-3 text-center font-bold text-orange-500">Mức 2 - Nguy Cơ Sớm</div>
                  <div className="p-3 text-center text-xs">Kết quả cận lâm sàng cận biên hoặc "tiền bệnh"</div>
                  <div className="p-3 text-xs">Khởi động can thiệp lối sống và/hoặc điều trị nhẹ để ngăn ngừa tiến triển bệnh</div>
                </div>

                <div className="grid grid-cols-4 bg-blue-50 border-t">
                  <div className="p-3 text-center font-semibold">3</div>
                  <div className="p-3 text-center font-bold text-red-600">Mức 3 - Bệnh Mạn Tính</div>
                  <div className="p-3 text-center text-xs">Đã xác định bệnh hoặc có triệu chứng rõ ràng</div>
                  <div className="p-3 text-xs">Điều trị liên tục, theo dõi chặt chẽ và điều chỉnh kịp thời</div>
                </div>
              </div>
            </section>

            {/* Rest of the trends tab content - keeping the same structure */}
            {/* ... */}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center">
            <Settings className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Cài Đặt Hệ Thống
            </h3>
            <p className="text-gray-500">
              Tùy chỉnh các thông số và cài đặt dashboard
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white border-t mt-8">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
            <span>© 2024 Hệ Thống Quản Lý Sức Khỏe</span>
            <span>•</span>
            <span>Phiên bản 1.0</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OriginalMedicalDashboard;